import React, { useCallback, useEffect, useReducer } from 'react'
import { AccountInfo, AuthenticationResult, CacheLookupPolicy, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { customerSignInRequest, forgotPasswordRequest } from './../../authConfig';
import { useMsal } from '@azure/msal-react';
import { UserContext, UserStatus, UserBusiness, UserProfile, KaEmployeeProfile, LoginType, Business } from './UserContext';
import useHandleForgotPassword from './msal/useHandleForgotPassword';
import useActiveAccount, { sameAccount } from './msal/useActiveAccount';

enum AuthStep {
  NotAuthenticated,
  Authenticated,
  AcquireUserProfile,
  UserDone,
  KaEmployeeDone,
  Error,
}

enum AuthAction {
  SignedIn,
  SignedOut,
  TokenReceived,
  EmployeeProfileReceived,
  UserProfileReceived,
  ErrorOccurred,
  UserBusinessSelected,
  EmployeeBusinessSelected
}

type State =
  | { step: AuthStep.NotAuthenticated, previousAccount?: AccountInfo }
  | { step: AuthStep.Authenticated, account: AccountInfo }
  | { step: AuthStep.AcquireUserProfile, account: AccountInfo, tokenResponse: AuthenticationResult }
  | { step: AuthStep.UserDone, account: AccountInfo, user: UserProfile }
  | { step: AuthStep.KaEmployeeDone, account: AccountInfo, employee: KaEmployeeProfile }
  | { step: AuthStep.Error }

type Action =
  | { type: AuthAction.SignedIn, account: AccountInfo }
  | { type: AuthAction.SignedOut, previousAccount?: AccountInfo }
  | { type: AuthAction.TokenReceived, account: AccountInfo, tokenResponse: AuthenticationResult }
  | { type: AuthAction.UserProfileReceived, userProfile: UserProfile }
  | { type: AuthAction.EmployeeProfileReceived, employee: KaEmployeeProfile }
  | { type: AuthAction.ErrorOccurred, error: string }
  | { type: AuthAction.UserBusinessSelected, selectedBusiness: UserBusiness | null }
  | { type: AuthAction.EmployeeBusinessSelected, selectedBusiness: Business | null }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case AuthAction.SignedIn:
      return { step: AuthStep.Authenticated, account: action.account };

    case AuthAction.SignedOut:
      return { step: AuthStep.NotAuthenticated, previousAccount: action.previousAccount };

    case AuthAction.TokenReceived:
      return { step: AuthStep.AcquireUserProfile, account: action.account, tokenResponse: action.tokenResponse };

    case AuthAction.EmployeeProfileReceived:
      if (state.step !== AuthStep.AcquireUserProfile) break;
      return { step: AuthStep.KaEmployeeDone, account: state.account, employee: action.employee };

    case AuthAction.UserProfileReceived:
      if (state.step !== AuthStep.AcquireUserProfile) break;
      return { step: AuthStep.UserDone, account: state.account, user: action.userProfile };

    case AuthAction.UserBusinessSelected:
      if (state.step !== AuthStep.UserDone) break;
      state.user.selectedBusiness = action.selectedBusiness;
      return { step: AuthStep.UserDone, account: state.account, user: state.user };

    case AuthAction.EmployeeBusinessSelected:
      if (state.step !== AuthStep.KaEmployeeDone) break;
      state.employee.selectedBusiness = action.selectedBusiness;
      return { step: AuthStep.KaEmployeeDone, account: state.account, employee: state.employee };
  }

  return { step: AuthStep.Error };
}

function currentAccount(state: State): AccountInfo | null {
  switch (state.step) {
    case AuthStep.Authenticated:
    case AuthStep.AcquireUserProfile:
    case AuthStep.UserDone:
    case AuthStep.KaEmployeeDone:
      return state.account;
    default:
      return null;
  }
}

type Props = {
  children?: React.ReactNode
};

export const UserProvider: React.FC<Props> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const [state, dispatch] = useReducer(reducer, { step: AuthStep.NotAuthenticated });
  const activeAccount = useActiveAccount(instance);

  useHandleForgotPassword(instance, forgotPasswordRequest);

  const justSignedIn = useCallback(() =>
    state.step === AuthStep.NotAuthenticated && !!activeAccount
    && !sameAccount(activeAccount, state.previousAccount ?? null)
    && inProgress === InteractionStatus.None,
    [activeAccount, state, inProgress]);

  const acquireTokenForAccount = useCallback((account: AccountInfo) => {
    const request = { ...customerSignInRequest, account, cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken };
    return instance.acquireTokenSilent(request)
      .catch(error => {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(request);
        }
        throw error;
      });
  }, [instance]);

  const logout = useCallback((account: AccountInfo) => {
    instance.logoutRedirect({
      account,
      postLogoutRedirectUri: window.location.pathname,
    });
  }, [instance]);

  useEffect(() => {
    if (state.step !== AuthStep.Authenticated) return;
    const account = state.account;
    acquireTokenForAccount(account)
      .then(tokenResponse => dispatch({ type: AuthAction.TokenReceived, account, tokenResponse }))
      .catch(() => dispatch({ type: AuthAction.SignedOut, previousAccount: account }));
  }, [state, acquireTokenForAccount]);

  useEffect(() => {
    if (justSignedIn()) {
      dispatch({ type: AuthAction.SignedIn, account: activeAccount as AccountInfo });
    }
  }, [justSignedIn, activeAccount])

  useEffect(() => {
    if (state.step === AuthStep.NotAuthenticated) return;

    const account = currentAccount(state);
    if (!activeAccount) {
      dispatch({ type: AuthAction.SignedOut });
    } else if (account && !sameAccount(activeAccount, account)) {
      logout(account);
    }
  }, [activeAccount, state, logout])

  const isKaEmployee = (tokenResponse: AuthenticationResult) => (tokenResponse.idTokenClaims as any).tfp === "B2C_1_Kahler_Employee";

  const fetchHeaders = async (account: AccountInfo) => {
    const token = await acquireTokenForAccount(account).then(r => r.accessToken);
    return new Headers({ 'Authorization': `Bearer ${token}` });
  }

  const fetchKaEmployeeHeaders = async (account: AccountInfo) => {
    try {
      return await fetchHeaders(account);
    } catch (error) {
      if (!(error instanceof InteractionRequiredAuthError)) logout(account);
      throw error;
    }
  }

  const loadUserProfile = useCallback(async () => {
    if (!activeAccount) return;
    const tokenResponse = await acquireTokenForAccount(activeAccount);
    dispatch({ type: AuthAction.TokenReceived, account: activeAccount, tokenResponse });
  }, [activeAccount, acquireTokenForAccount]);

  const loadEmployeeProfile = useCallback(async (account: AccountInfo, tokenResponse: AuthenticationResult) => {
    const headers = new Headers({ 'Authorization': `Bearer ${tokenResponse.accessToken}` });
    const userInfo = await fetch('/employeeApi/users/me', { headers }).then(response => response.json());
    const businesses = userInfo.businesses.map((b: any) => ({ id: b.id, name: b.name, integrations: b.integrations ?? [] }));
    dispatch({
      type: AuthAction.EmployeeProfileReceived,
      employee: {
        id: tokenResponse.uniqueId,
        email: (tokenResponse.idTokenClaims as any).emails[0],
        userType: LoginType.KaEmployee,
        fetchHeaders: () => fetchKaEmployeeHeaders(account),
        signOut: () => logout(account),
        canEditBusinesses: userInfo.canEditBusinesses,
        canViewNotifications: userInfo.canViewNotifications,
        canViewSiteStatus: userInfo.canViewSiteStatus,
        canViewOrders: userInfo.canViewOrders,
        canViewIntegrations: userInfo.canViewIntegrations,
        businesses,
        selectedBusiness: initialSelectedEmployeeBusiness(businesses),
        setSelectedBusiness: setSelectedEmployeeBusiness,
        refreshUser: async () => loadEmployeeProfile(account, await acquireTokenForAccount(account))
      }
    });
  }, [acquireTokenForAccount]);

  useEffect(() => {
    if (state.step === AuthStep.AcquireUserProfile) {
      if (isKaEmployee(state.tokenResponse)) {
        loadEmployeeProfile(state.account, state.tokenResponse);
      } else {
        const headers = new Headers({ 'Authorization': `Bearer ${state.tokenResponse.accessToken}` });
        fetch('/api/users/me', { headers })
          .then(response => response.json())
          .then(userInfo => {
            const businesses = userInfo.businesses.map((b: any) => ({
              id: b.id,
              name: b.name,
              administrator: b.administrator,
              userRole: b.userRole,
              siteAllowance: b.siteAllowance,
              numberOfSites: b.numberOfSites,
              usesDataSharing: b.usesDataSharing,
              integrations: b.integrations,
              disabled: b.disabled,
              sandbox: b.sandbox,
              siteRestricted: b.siteRestricted ?? true,
              assignedSites: b.assignedSites ?? [],
            } as UserBusiness));

            dispatch({
              type: AuthAction.UserProfileReceived,
              userProfile: {
                id: userInfo.id,
                userType: LoginType.User,
                userAgreementInfo: {
                  hasAcceptedUserAgreement: userInfo.userAgreementInfo.hasAcceptedUserAgreement,
                  url: userInfo.userAgreementInfo.url
                },
                isDevelopment: userInfo.isDevelopment,
                email: userInfo.emailAddress,
                hasConflictingSignInMethod: userInfo.hasConflictingSignInMethod ?? false,
                fetchHeaders: () => fetchHeaders(state.account),
                signOut: () => logout(state.account),
                businesses,
                selectedBusiness: initialSelectedBusiness(businesses),
                setSelectedBusiness: setSelectedUserBusiness
              }
            })
          });
      }
    }
  }, [state, acquireTokenForAccount])

  const initialSelectedBusiness = (businesses: UserBusiness[]): UserBusiness | null => {
    if (businesses.length > 0) {
      const storedId = localStorage.getItem('selectedBusinessId');
      const stored = storedId ? businesses.find(b => b.id === storedId) : null;
      return stored ?? [...businesses].sort((a, b) => Number(a.sandbox) - Number(b.sandbox) || a.name.localeCompare(b.name))[0];
    }
    return null;
  }

  const initialSelectedEmployeeBusiness = (businesses: Business[]): Business | null => {
    if (businesses.length > 0) {
      const storedId = localStorage.getItem('selectedEmployeeBusinessId');
      const stored = storedId ? businesses.find(b => b.id === storedId) : null;
      return stored ?? businesses.sort((a, b) => a.name.localeCompare(b.name))[0];
    }
    return null;
  }

  function setSelectedUserBusiness(business: UserBusiness | null) {
    if (business) localStorage.setItem('selectedBusinessId', business.id);
    else localStorage.removeItem('selectedBusinessId');
    dispatch({ type: AuthAction.UserBusinessSelected, selectedBusiness: business });
  }

  function setSelectedEmployeeBusiness(business: Business | null) {
    if (business) localStorage.setItem('selectedEmployeeBusinessId', business.id);
    else localStorage.removeItem('selectedEmployeeBusinessId');
    dispatch({ type: AuthAction.EmployeeBusinessSelected, selectedBusiness: business });
  }

  function toUserStatus(state: State): UserStatus {
    const defaultStatus = {
      busy: inProgress !== InteractionStatus.None,
      user: null,
      refreshUser: loadUserProfile,
      login: () => instance.loginRedirect(customerSignInRequest),
    }
    switch (state.step) {
      case AuthStep.UserDone:
        return { ...defaultStatus, user: state.user };
      case AuthStep.KaEmployeeDone:
        return { ...defaultStatus, user: state.employee };
      case AuthStep.Authenticated:
      case AuthStep.AcquireUserProfile:
        return { ...defaultStatus, busy: true };
      case AuthStep.NotAuthenticated:
        return { ...defaultStatus, busy: defaultStatus.busy || justSignedIn() }
      case AuthStep.Error:
      default:
        return defaultStatus;
    }
  }

  return (
    <UserContext.Provider value={toUserStatus(state)}>
      {children}
    </UserContext.Provider>
  );
};
